import { router } from 'expo-router';
import { PhoneCall, Plus } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { FadeIn } from '../../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../../src/components/primitives';
import { Button } from '../../../src/components/primitives/Button';
import { EmptyState } from '../../../src/components/primitives/EmptyState';
import { Select } from '../../../src/components/primitives/Select';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useEdgeToEdge } from '../../../src/contexts/LayoutChromeContext';
import { useTranslation } from '../../../src/contexts/TranslationContext';
import { AgentCard } from '../../../src/features/voice-agents/components/AgentCard';
import { AgentListSkeleton } from '../../../src/features/voice-agents/components/AgentListSkeleton';
import { DeleteAgentDialog } from '../../../src/features/voice-agents/components/DeleteAgentDialog';
import { VoiceSectionTabs } from '../../../src/features/voice-agents/components/VoiceSectionTabs';
import {
  LANGUAGE_OPTION_VALUES,
  USE_CASE_OPTION_VALUES,
} from '../../../src/features/voice-agents/constants';
import {
  useArchiveVoiceAgent,
  useDuplicateVoiceAgent,
  useVoiceAgents,
} from '../../../src/features/voice-agents/hooks';
import type {
  ListVoiceAgentsFilters,
  VoiceAgent,
  VoiceAgentLanguage,
  VoiceAgentStatus,
  VoiceAgentUseCase,
} from '../../../src/features/voice-agents/types';
import { colors } from '../../../src/theme/colors';
import { tokens } from '../../../src/theme/tokens';

function Separator() {
  return <View style={{ height: 8 }} />;
}

type AgentOrPlaceholder = VoiceAgent | { id: string; __placeholder: true };

function padAgentsForGrid(agents: VoiceAgent[], columns: number): AgentOrPlaceholder[] {
  if (columns <= 1 || agents.length % columns === 0) return agents;
  return [
    ...agents,
    ...Array.from({ length: columns - (agents.length % columns) }, (_, index) => ({
      id: `__agent_placeholder_${String(index)}`,
      __placeholder: true as const,
    })),
  ];
}

export default function VoiceAgentsScreen() {
  useEdgeToEdge();
  const { t } = useTranslation();
  const { width: viewportWidth } = useWindowDimensions();
  const numColumns = viewportWidth >= 1280 ? 3 : viewportWidth >= tokens.breakpoints.tablet ? 2 : 1;

  const STATUS_OPTIONS: Array<{ label: string; value: VoiceAgentStatus | '' }> = [
    { label: t('voiceAgents.list.filterAll'), value: '' },
    { label: t('voiceAgents.list.filterDraft'), value: 'draft' },
    { label: t('voiceAgents.list.filterPublished'), value: 'published' },
    { label: t('voiceAgents.list.filterArchived'), value: 'archived' },
  ];

  const USE_CASE_OPTIONS: Array<{ label: string; value: VoiceAgentUseCase | '' }> = [
    { label: t('voiceAgents.list.filterAllUseCases'), value: '' },
    ...USE_CASE_OPTION_VALUES.map((v) => ({
      label: t(`voiceAgents.wizard.useCase.${v}`),
      value: v,
    })),
  ];

  const LANGUAGE_OPTIONS: Array<{ label: string; value: VoiceAgentLanguage | '' }> = [
    { label: t('voiceAgents.list.filterAllLanguages'), value: '' },
    ...LANGUAGE_OPTION_VALUES.map((v) => ({
      label: t(`voiceAgents.wizard.language.${v}`),
      value: v,
    })),
  ];

  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? '';

  const [statusFilter, setStatusFilter] = useState<VoiceAgentStatus | ''>('');
  const [useCaseFilter, setUseCaseFilter] = useState<VoiceAgentUseCase | ''>('');
  const [languageFilter, setLanguageFilter] = useState<VoiceAgentLanguage | ''>('');
  const [agentToDelete, setAgentToDelete] = useState<VoiceAgent | null>(null);

  const filters: ListVoiceAgentsFilters = useMemo(() => {
    const f: ListVoiceAgentsFilters = {};
    if (statusFilter) f.status = statusFilter as VoiceAgentStatus;
    if (useCaseFilter) f.useCase = useCaseFilter as VoiceAgentUseCase;
    if (languageFilter) f.language = languageFilter as VoiceAgentLanguage;
    return f;
  }, [statusFilter, useCaseFilter, languageFilter]);

  const { data: agents = [], isLoading, isError, refetch } = useVoiceAgents(orgId, filters);
  const archiveAgent = useArchiveVoiceAgent();
  const duplicateAgent = useDuplicateVoiceAgent();

  const handlePress = useCallback((agent: VoiceAgent) => {
    router.push(`/voice-agents/${agent.id}`);
  }, []);

  const handleDuplicate = useCallback(
    (agent: VoiceAgent) => {
      duplicateAgent.mutate({ id: agent.id, organizationId: orgId });
    },
    [duplicateAgent, orgId],
  );

  const handleEdit = useCallback((agent: VoiceAgent) => {
    router.push(`/voice-agents/${agent.id}/edit`);
  }, []);

  const handleArchive = useCallback(
    (agent: VoiceAgent) => {
      archiveAgent.mutate({ id: agent.id, organizationId: orgId });
    },
    [archiveAgent, orgId],
  );

  const handleDelete = useCallback((agent: VoiceAgent) => {
    setAgentToDelete(agent);
  }, []);

  const total = agents.length;
  const filtersActive = !!statusFilter || !!useCaseFilter || !!languageFilter;

  return (
    <View testID="voice_agents_screen" style={styles.root}>
      <View style={styles.content}>
        <FadeIn delay={0} translateY={12} style={styles.sectionSpacing}>
          <HStack justify="between" align="center" wrap style={styles.toolbarRow}>
            <VStack spacing="xs" style={styles.toolbarTitle}>
              <Text variant="h4" color={colors.text.primary} style={styles.heading}>
                {t('voiceAgents.list.title')}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {`${total} ${total === 1 ? t('voiceAgents.list.countSingular') : t('voiceAgents.list.countPlural')}`}
              </Text>
            </VStack>

            <HStack spacing="sm" style={styles.toolbarActions} wrap>
              <Button
                testID="voice_agents_new_button"
                variant="primary"
                size="sm"
                onPress={() => router.push('/voice-agents/new')}
                accessibilityLabel={t('voiceAgents.list.newAgentA11y')}
              >
                <HStack spacing="xs" align="center">
                  <Plus size={14} color={colors.background.primary} strokeWidth={2.5} />
                  <Text variant="caption" color={colors.background.primary} style={styles.newLabel}>
                    {t('voiceAgents.list.newAgent')}
                  </Text>
                </HStack>
              </Button>
            </HStack>
          </HStack>
        </FadeIn>

        <FadeIn delay={40} translateY={10} style={styles.sectionSpacing}>
          <VoiceSectionTabs />
        </FadeIn>

        <FadeIn delay={80} translateY={10} style={styles.sectionSpacing}>
          <Select
            variant="chips"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as VoiceAgentStatus | '')}
            testID="voice-agents-status-filter"
          />
        </FadeIn>

        <FadeIn delay={100} translateY={10} style={styles.sectionSpacing}>
          <HStack spacing="sm" wrap style={styles.dropdownFiltersRow}>
            <Box style={styles.dropdownFilter}>
              <Select
                variant="dropdown"
                options={USE_CASE_OPTIONS}
                value={useCaseFilter}
                onChange={(v) => setUseCaseFilter(v as VoiceAgentUseCase | '')}
                placeholder={t('voiceAgents.list.filterUseCaseLabel')}
                testID="voice-agents-usecase-filter"
              />
            </Box>
            <Box style={styles.dropdownFilter}>
              <Select
                variant="dropdown"
                options={LANGUAGE_OPTIONS}
                value={languageFilter}
                onChange={(v) => setLanguageFilter(v as VoiceAgentLanguage | '')}
                placeholder={t('voiceAgents.list.filterLanguageLabel')}
                testID="voice-agents-language-filter"
              />
            </Box>
          </HStack>
        </FadeIn>

        <Box flex={1}>
          {isLoading && <AgentListSkeleton columns={numColumns} />}
          {!isLoading && isError && (
            <View
              testID="voice-agents-error"
              style={styles.centerState}
              accessible
              accessibilityLabel={t('voiceAgents.list.errorTitle')}
            >
              <EmptyState
                icon={<PhoneCall size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
                title={t('voiceAgents.list.errorTitle')}
                description={t('voiceAgents.list.errorDescription')}
              />
              <Button
                testID="voice-agents-retry"
                variant="secondary"
                onPress={() => refetch()}
                accessibilityLabel={t('voiceAgents.list.retry')}
              >
                {t('voiceAgents.list.retry')}
              </Button>
            </View>
          )}
          {!isLoading && !isError && agents.length === 0 && (
            <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
              <EmptyState
                icon={<PhoneCall size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
                title={
                  filtersActive
                    ? t('voiceAgents.list.emptyMatchTitle')
                    : t('voiceAgents.list.emptyTitle')
                }
                description={
                  filtersActive
                    ? t('voiceAgents.list.emptyMatchDescription')
                    : t('voiceAgents.list.emptyDescription')
                }
                action={
                  filtersActive
                    ? undefined
                    : {
                        label: t('voiceAgents.list.startOnboarding'),
                        onPress: () => router.push('/voice-agents/onboarding'),
                      }
                }
              />
            </Box>
          )}
          {!isLoading && !isError && agents.length > 0 && (
            <FlatList<AgentOrPlaceholder>
              testID="voice_agents_list"
              key={`voice-agents-${String(numColumns)}`}
              data={padAgentsForGrid(agents, numColumns)}
              keyExtractor={(a) => a.id}
              numColumns={numColumns}
              columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
              renderItem={({ item }) => {
                if ('__placeholder' in item) return <View style={styles.gridItem} />;
                const card = (
                  <AgentCard
                    agent={item}
                    onPress={handlePress}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onArchive={handleArchive}
                    onDelete={handleDelete}
                  />
                );
                return numColumns > 1 ? <View style={styles.gridItem}>{card}</View> : card;
              }}
              ItemSeparatorComponent={numColumns === 1 ? Separator : undefined}
              contentContainerStyle={styles.listContent}
              contentInsetAdjustmentBehavior="automatic"
              accessibilityLabel={t('voiceAgents.list.listA11y')}
            />
          )}
        </Box>
      </View>

      <DeleteAgentDialog
        agent={agentToDelete}
        organizationId={orgId}
        onClose={() => setAgentToDelete(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    position: 'relative',
    ...(Platform.OS === 'web'
      ? ({ overflow: 'hidden' as const, width: '100%', maxWidth: '100%' } as ViewStyle)
      : {}),
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    ...(Platform.OS === 'web'
      ? ({ minWidth: 0, width: '100%', maxWidth: '100%' } as ViewStyle)
      : {}),
  },
  toolbarRow: {
    rowGap: tokens.spacing.sm,
    width: '100%',
  },
  toolbarTitle: {
    flexShrink: 1,
    minWidth: 0,
  },
  toolbarActions: {
    flexShrink: 0,
    flexWrap: 'wrap',
    rowGap: tokens.spacing.xs,
  },
  sectionSpacing: {
    marginBottom: tokens.spacing.lg,
  },
  heading: {
    fontWeight: tokens.fontWeight.bold,
  },
  newLabel: {
    fontWeight: '600',
    marginLeft: Platform.OS === 'web' ? 0 : 4,
  },
  listContent: {
    paddingVertical: 4,
    paddingBottom: tokens.spacing['3xl'],
  },
  columnWrapper: {
    marginBottom: tokens.spacing.md,
    columnGap: tokens.spacing.md,
  },
  gridItem: {
    flex: 1,
    flexBasis: 0,
    alignSelf: 'stretch',
    minWidth: 0,
    maxWidth: '100%',
  },
  centerState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing.xxl,
  },
  dropdownFiltersRow: {
    rowGap: tokens.spacing.sm,
    width: '100%',
  },
  dropdownFilter: {
    flex: 1,
    minWidth: 140,
  },
});
